import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AdminService } from '@core/services/admin.service';
import { ErrorHandlerService } from '@core/services/errorHandler/error-handler.service';
import { AdminSeriesConfig } from '@shared/models/filetype/AdminSeriesConfig';
import { PreflashMonitoringApiItem, PreflashMonitoringContent, PreflashMonitoringEcu } from '@shared/models/PreflashMonitoring';
import { PreflashMonitoringService } from '@core/services/preflash-monitoring.service';
import { finalize } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-fcsc-admin-preflash-monitoring',
  templateUrl: './fcsc-admin-preflash-monitoring.html',
  styleUrls: ['./fcsc-admin-preflash-monitoring.scss'],
})
export class FcscAdminPreflashMonitoringComponent implements OnInit {

  form!: UntypedFormGroup;
  preflashData: PreflashMonitoringContent[] = [];
  seriesList: { brName: string; value: string }[] = [];
  statusList = [
    { label: '', value: '' },
    { label: 'Ready', value: 'ready' },
    { label: 'Pending', value: 'pending' },
    { label: 'Queued', value: 'queued' },
    { label: 'Error', value: 'error' },
    { label: 'Recalculation', value: 'recalculation' },
  ];
  systemList = [
    { label: '', value: '' },
    { label: 'Xentry Order', value: 'Xentry Order' },
    { label: 'Xentry Workshop', value: 'Xentry Workshop' },
  ];
  totalItems = 0;
  currentPage = 1;
  pageSize = 100;
  isLoading = false;
  selectedCalculationResult?: PreflashMonitoringContent & { ecuList: PreflashMonitoringEcu[] };
  @ViewChild('calculationDetailsModal') calculationDetailsModal!: TemplateRef<any>;

  constructor (
      private readonly formBuilder: UntypedFormBuilder,
      private readonly modalService: NgbModal,
      private readonly adminService: AdminService,
      private readonly preflashMonitoringService: PreflashMonitoringService,
      private readonly notificationService: ErrorHandlerService,
    ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.loadSeries();
    this.loadPage(1);
  }

  private loadSeries(): void {
    this.adminService.getSeriesConfig().subscribe((series: AdminSeriesConfig[]) => {
      const seriesNames = [...new Set(series.map(({ brName }) => brName))].sort();
      this.seriesList = [
        { brName: '', value: '' },
        ...seriesNames.map((brName) => ({ brName, value: brName })),
      ];
    });
  }

  private loadPage(page: number): void {
    this.isLoading = true;
    this.preflashMonitoringService.getCalculationStatus(page, this.pageSize)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe((response) => {
        this.currentPage = response.page;
        this.totalItems = response.total;
        this.preflashData = response.items.map((item) => this.toContent(item));
      });
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      seriesFilter: '',
      vinFilter: '',
      appointmentDateFilter: null,
      calculationTimeFilter: null,
      statusFilter: null,
      systemFilter: null,
      sdpFilter: '',
      vp5VersionFilter: '',
      asTactFilter: '',
    });
  }

  clearDateFilter(controlName: 'appointmentDateFilter' | 'calculationTimeFilter'): void {
    this.form.get(controlName)?.setValue(null);
  }

  private toContent(item: PreflashMonitoringApiItem): PreflashMonitoringContent {
    return {
      series: item.series,
      vin: item.vin,
      appointment_date: item.appointment_date,
      calculation_time: item.calculation_time,
      status: item.status.toLowerCase() as PreflashMonitoringContent['status'],
      system: item.system,
      sdp: item.sdp,
      vp5_version: item.vp5,
      as_tact: item.as_takt,
    };
  }

  setPage(pageInfo: { page: number }): void {
    this.loadPage(pageInfo.page);
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadPage(1);
  }

  get pageStart(): number {
    return this.totalItems ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  openCalculationDetails(calculationResult: PreflashMonitoringContent): void {
    if (this.isLoading) {
      return;
    }

    this.selectedCalculationResult = undefined;
    this.isLoading = true;
    this.preflashMonitoringService.getCalculationDetails(calculationResult.vin)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result) => {
          this.selectedCalculationResult = {
            ...this.toContent({ ...result.response, status: result.status }),
            ecuList: result.response.ecuList,
            error_message: result.status.toLowerCase() === 'error' ? result.message : '',
          };
          this.modalService.open(this.calculationDetailsModal, {
            size: 'lg',
            backdrop: 'static',
            keyboard: false,
          });
        },
        error: (error) => this.notificationService.handleServerErrorNotification(
          error, 'modules.admin-section.config-modal.insert-edit-api-error-message'
        ),
      });
  }

}
